/**
 * Baby Appointments Data
 *
 * Contains all predefined appointment suggestions for baby mode:
 * - U-Untersuchungen (U1-U9)
 * - Impfungen (STIKO recommendations)
 * - Behördengänge (birth certificate, benefits, etc.)
 * - Kurse (postpartum courses, baby swimming, etc.)
 * - Mutter-Nachsorge (midwife, gynecologist)
 */

export type SuggestionType =
  | 'u_examination'
  | 'vaccination'
  | 'official'
  | 'course'
  | 'maternal_care';

export interface BabyAppointmentTemplate {
  suggestion_type: SuggestionType;
  title: string;
  description: string;
  /**
   * Days from birth when this becomes relevant
   * Use negative numbers for items that should be done before birth
   */
  days_from_birth_start: number;
  /**
   * Days from birth when this is no longer recommended (optional)
   */
  days_from_birth_end?: number;
  /**
   * Priority for sorting (higher = more important)
   */
  priority: number;
  /**
   * Order within same category
   */
  category_order: number;
}

// ============================================================================
// U-UNTERSUCHUNGEN
// According to German "Gelbes Heft" (Kinder-Untersuchungsheft)
// ============================================================================

export const U_EXAMINATIONS: BabyAppointmentTemplate[] = [
  {
    suggestion_type: 'u_examination',
    title: 'U1 - Neugeborenen-Erstuntersuchung',
    description:
      'Direkt nach der Geburt im Krankenhaus. APGAR-Test, erste Reflexe, Vitamin K.',
    days_from_birth_start: 0,
    days_from_birth_end: 0,
    priority: 100,
    category_order: 1,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U2 - Neugeborenen-Basisuntersuchung',
    description:
      '3.-10. Lebenstag. Stoffwechselscreening (Fersenblut), Hörtest, Reflexe. Meist noch im Krankenhaus.',
    days_from_birth_start: 3,
    days_from_birth_end: 10,
    priority: 100,
    category_order: 2,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U3 - Erste Kinderarzt-Untersuchung',
    description:
      '4.-5. Lebenswoche. Hüftultraschall, Reflexe, Gewicht, Größe. Erste Vorsorge beim Kinderarzt.',
    days_from_birth_start: 28,
    days_from_birth_end: 35,
    priority: 95,
    category_order: 3,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U4 - 3.-4. Lebensmonat',
    description:
      'Motorische Entwicklung, Hören, Sehen. Besprechung der ersten Impfungen.',
    days_from_birth_start: 90, // ~3 months
    days_from_birth_end: 120, // ~4 months
    priority: 90,
    category_order: 4,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U5 - 6.-7. Lebensmonat',
    description:
      'Körperliche und geistige Entwicklung, Beweglichkeit, Sehtest, Sprachentwicklung.',
    days_from_birth_start: 180, // 6 months
    days_from_birth_end: 210, // 7 months
    priority: 85,
    category_order: 5,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U6 - 10.-12. Lebensmonat',
    description:
      'Um den 1. Geburtstag. Sprache, Bewegung, Sozialverhalten, Hör- und Sehtest.',
    days_from_birth_start: 300, // 10 months
    days_from_birth_end: 365, // 12 months
    priority: 85,
    category_order: 6,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U7 - 21.-24. Lebensmonat',
    description:
      'Um den 2. Geburtstag. Sprachentwicklung, Gang, Verhalten, Zähne.',
    days_from_birth_start: 630, // 21 months
    days_from_birth_end: 730, // 24 months
    priority: 80,
    category_order: 7,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U7a - 34.-36. Lebensmonat',
    description:
      'Um den 3. Geburtstag. Sehtest, Sprachentwicklung, Sozialverhalten.',
    days_from_birth_start: 1020, // 34 months
    days_from_birth_end: 1095, // 36 months
    priority: 75,
    category_order: 8,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U8 - 46.-48. Lebensmonat',
    description:
      'Um den 4. Geburtstag. Fein- und Grobmotorik, Sprache, Sozialverhalten, Sehtest.',
    days_from_birth_start: 1380, // 46 months
    days_from_birth_end: 1460, // 48 months
    priority: 70,
    category_order: 9,
  },
  {
    suggestion_type: 'u_examination',
    title: 'U9 - 60.-64. Lebensmonat',
    description:
      'Einschuluntersuchung (5 Jahre). Schulreife, Seh- und Hörtest, Motorik, Sprache.',
    days_from_birth_start: 1800, // 60 months
    days_from_birth_end: 1920, // 64 months
    priority: 70,
    category_order: 10,
  },
];

// ============================================================================
// IMPFUNGEN (STIKO Empfehlungen)
// Based on German STIKO (Ständige Impfkommission) recommendations
// ============================================================================

export const VACCINATIONS: BabyAppointmentTemplate[] = [
  {
    suggestion_type: 'vaccination',
    title: 'Rotaviren - 1. Impfung',
    description:
      'Schluckimpfung gegen Rotaviren. Ab 6 Wochen, spätestens bis 12 Wochen beginnen.',
    days_from_birth_start: 42, // 6 weeks
    days_from_birth_end: 84, // 12 weeks max start
    priority: 90,
    category_order: 1,
  },
  {
    suggestion_type: 'vaccination',
    title: 'Rotaviren - 2. Impfung',
    description:
      'Zweite Schluckimpfung gegen Rotaviren. Ca. 4 Wochen nach der ersten.',
    days_from_birth_start: 56, // 8 weeks
    days_from_birth_end: 112, // 16 weeks
    priority: 88,
    category_order: 2,
  },
  {
    suggestion_type: 'vaccination',
    title: '6-fach + Pneumokokken - 1. Impfung',
    description:
      '6-fach (Diphtherie, Tetanus, Keuchhusten, Polio, Hib, Hepatitis B) + Pneumokokken. Ab 2 Monaten.',
    days_from_birth_start: 60, // 2 months
    days_from_birth_end: 90, // 3 months
    priority: 95,
    category_order: 3,
  },
  {
    suggestion_type: 'vaccination',
    title: '6-fach + Pneumokokken - 2. Impfung',
    description: 'Zweite Impfung mit 4 Monaten.',
    days_from_birth_start: 120, // 4 months
    days_from_birth_end: 150, // 5 months
    priority: 93,
    category_order: 4,
  },
  {
    suggestion_type: 'vaccination',
    title: '6-fach + Pneumokokken - 3. Impfung (Auffrischung)',
    description:
      'Auffrischungsimpfung mit 11 Monaten. Mindestens 6 Monate nach der 2. Impfung.',
    days_from_birth_start: 330, // 11 months
    days_from_birth_end: 395, // 13 months
    priority: 91,
    category_order: 5,
  },
  {
    suggestion_type: 'vaccination',
    title: 'MMR + Varizellen - 1. Impfung',
    description:
      'Masern-Mumps-Röteln + Windpocken. Ab 11 Monaten, spätestens vor dem 14. Monat.',
    days_from_birth_start: 330, // 11 months
    days_from_birth_end: 425, // 14 months
    priority: 90,
    category_order: 6,
  },
  {
    suggestion_type: 'vaccination',
    title: 'Meningokokken C - Impfung',
    description:
      'Schutz gegen Meningokokken C (Hirnhautentzündung). Ab 12 Monaten.',
    days_from_birth_start: 365, // 12 months
    days_from_birth_end: 730, // 24 months
    priority: 85,
    category_order: 7,
  },
  {
    suggestion_type: 'vaccination',
    title: 'MMR + Varizellen - 2. Impfung',
    description:
      'Zweite Masern-Mumps-Röteln + Windpocken-Impfung. Ab 15 Monaten, spätestens vor dem 23. Monat.',
    days_from_birth_start: 450, // 15 months
    days_from_birth_end: 700, // 23 months
    priority: 88,
    category_order: 8,
  },
];

// ============================================================================
// BEHÖRDENGÄNGE
// Administrative tasks after birth
// ============================================================================

export const OFFICIAL_APPOINTMENTS: BabyAppointmentTemplate[] = [
  {
    suggestion_type: 'official',
    title: 'Geburtsurkunde beantragen',
    description:
      'Beim Standesamt innerhalb von 7 Tagen nach der Geburt. Personalausweis, Geburtsbescheinigung vom Krankenhaus mitnehmen.',
    days_from_birth_start: 0,
    days_from_birth_end: 7,
    priority: 100,
    category_order: 1,
  },
  {
    suggestion_type: 'official',
    title: 'Baby bei Krankenversicherung anmelden',
    description:
      'Bei der Krankenkasse anrufen/online anmelden. Geburtsurkunde bereithalten. Zeitnah erledigen!',
    days_from_birth_start: 0,
    days_from_birth_end: 30,
    priority: 95,
    category_order: 2,
  },
  {
    suggestion_type: 'official',
    title: 'Vaterschaftsanerkennung',
    description:
      'Wenn nicht verheiratet: Beim Jugendamt oder Standesamt. Kann auch vor der Geburt erfolgen.',
    days_from_birth_start: -30, // Can be done before birth
    days_from_birth_end: 365,
    priority: 90,
    category_order: 3,
  },
  {
    suggestion_type: 'official',
    title: 'Kindergeld beantragen',
    description:
      'Bei der Familienkasse (Arbeitsagentur) - online, per Post oder persönlich. Rückwirkend bis zu 6 Monate.',
    days_from_birth_start: 0,
    days_from_birth_end: 180, // 6 months retroactive
    priority: 85,
    category_order: 4,
  },
  {
    suggestion_type: 'official',
    title: 'Elterngeld beantragen',
    description:
      'Bei der Elterngeldstelle des Bundeslandes. Online oder per Post. Rückwirkend max. 3 Monate!',
    days_from_birth_start: 0,
    days_from_birth_end: 90, // 3 months retroactive!
    priority: 95,
    category_order: 5,
  },
  {
    suggestion_type: 'official',
    title: 'Kinderreisepass beantragen',
    description:
      'Falls eine Reise geplant ist. Beim Bürgeramt mit Passfoto, Geburtsurkunde und Ausweisen der Eltern.',
    days_from_birth_start: 30,
    days_from_birth_end: undefined, // No end
    priority: 30,
    category_order: 6,
  },
];

// ============================================================================
// KURSE FÜR ELTERN
// Parent courses and activities
// ============================================================================

export const PARENT_COURSES: BabyAppointmentTemplate[] = [
  {
    suggestion_type: 'course',
    title: 'Rückbildungsgymnastik anmelden',
    description:
      'Für die Mutter, ab 6-8 Wochen nach der Geburt (nach Kaiserschnitt später). Wird von der Krankenkasse bezahlt.',
    days_from_birth_start: 42, // 6 weeks
    days_from_birth_end: 270, // 9 months max (Kassenleistung)
    priority: 70,
    category_order: 1,
  },
  {
    suggestion_type: 'course',
    title: 'Babymassage-Kurs anmelden',
    description:
      'Fördert Bindung und Entspannung. Ab ca. 4-6 Wochen geeignet.',
    days_from_birth_start: 28, // 4 weeks
    days_from_birth_end: 180, // 6 months
    priority: 50,
    category_order: 2,
  },
  {
    suggestion_type: 'course',
    title: 'PEKiP / DELFI / Babykurs anmelden',
    description:
      'Prager-Eltern-Kind-Programm oder DELFI. Spielen, Bewegung, Austausch mit anderen Eltern. Ab 4-6 Wochen.',
    days_from_birth_start: 28, // 4 weeks
    days_from_birth_end: 365, // 1 year
    priority: 50,
    category_order: 3,
  },
  {
    suggestion_type: 'course',
    title: 'Babyschwimmen anmelden',
    description:
      'Wassergewöhnung und Spaß im Wasser. Ab 3 Monaten, nach abgeschlossener Grundimmunisierung.',
    days_from_birth_start: 90, // 3 months
    days_from_birth_end: 365, // 1 year
    priority: 45,
    category_order: 4,
  },
  {
    suggestion_type: 'course',
    title: 'Erste-Hilfe-Kurs am Kind',
    description:
      'Lebensrettende Maßnahmen für Babys und Kleinkinder. Sollte jeder Elternteil machen!',
    days_from_birth_start: 0, // Anytime
    days_from_birth_end: undefined, // No end
    priority: 75,
    category_order: 5,
  },
];

// ============================================================================
// MUTTER-NACHSORGE
// Postpartum care for the mother
// ============================================================================

export const MATERNAL_CARE: BabyAppointmentTemplate[] = [
  {
    suggestion_type: 'maternal_care',
    title: 'Hebammen-Nachsorge',
    description:
      'Hausbesuche durch die Hebamme. Erste Wochen täglich möglich, dann nach Bedarf. Bis 12 Wochen Kassenleistung.',
    days_from_birth_start: 0,
    days_from_birth_end: 84, // 12 weeks
    priority: 90,
    category_order: 1,
  },
  {
    suggestion_type: 'maternal_care',
    title: 'Gynäkologische Nachuntersuchung',
    description:
      'Kontrolle beim Frauenarzt ca. 6-8 Wochen nach der Geburt. Rückbildung, Verhütung, allgemeiner Check.',
    days_from_birth_start: 42, // 6 weeks
    days_from_birth_end: 70, // 10 weeks
    priority: 85,
    category_order: 2,
  },
];

// ============================================================================
// COMBINED EXPORT
// ============================================================================

export const ALL_BABY_APPOINTMENTS: BabyAppointmentTemplate[] = [
  ...U_EXAMINATIONS,
  ...VACCINATIONS,
  ...OFFICIAL_APPOINTMENTS,
  ...PARENT_COURSES,
  ...MATERNAL_CARE,
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate appointment dates based on birth date
 */
export function calculateAppointmentDates(
  template: BabyAppointmentTemplate,
  birthDate: Date
): { recommended_from: Date; recommended_until: Date | null } {
  const from = new Date(birthDate);
  from.setDate(from.getDate() + template.days_from_birth_start);

  let until: Date | null = null;
  if (template.days_from_birth_end !== undefined && template.days_from_birth_end !== null) {
    until = new Date(birthDate);
    until.setDate(until.getDate() + template.days_from_birth_end);
  }

  return {
    recommended_from: from,
    recommended_until: until,
  };
}

/**
 * Generate all appointment suggestions for a baby
 */
export function generateBabyAppointments(birthDate: Date): Array<{
  suggestion_type: SuggestionType;
  title: string;
  description: string;
  recommended_from: Date;
  recommended_until: Date | null;
  priority: number;
  category_order: number;
}> {
  return ALL_BABY_APPOINTMENTS.map((template) => {
    const dates = calculateAppointmentDates(template, birthDate);
    return {
      suggestion_type: template.suggestion_type,
      title: template.title,
      description: template.description,
      recommended_from: dates.recommended_from,
      recommended_until: dates.recommended_until,
      priority: template.priority,
      category_order: template.category_order,
    };
  });
}

/**
 * Get appointments filtered by type
 */
export function getAppointmentsByType(type: SuggestionType): BabyAppointmentTemplate[] {
  return ALL_BABY_APPOINTMENTS.filter((a) => a.suggestion_type === type);
}

/**
 * Get type label for display
 */
export function getSuggestionTypeLabel(type: SuggestionType): string {
  const labels: Record<SuggestionType, string> = {
    u_examination: 'U-Untersuchung',
    vaccination: 'Impfung',
    official: 'Behördengang',
    course: 'Kurs',
    maternal_care: 'Nachsorge Mutter',
  };
  return labels[type] || type;
}

/**
 * Get type icon for display
 */
export function getSuggestionTypeIcon(type: SuggestionType): string {
  const icons: Record<SuggestionType, string> = {
    u_examination: '👶',
    vaccination: '💉',
    official: '📋',
    course: '🏊',
    maternal_care: '🤱',
  };
  return icons[type] || '📅';
}
